import React from "react";
import { useTranslation } from "react-i18next";
import { indexStore } from "src/store/indexStore";
import { AlertDialogs } from "../Dialgos/Alter-Daylogs";
import { Skeleton } from "../ui/skeleton";
import EditCustomerModel from "../pages/customers/EditCustomerModel";
import { useEffect } from "react";
import Visibility from "../utils/Visibility";
import { userTypes } from "src/enums/usersTpes";
import { Card, CardContent, CardHeader } from "../ui/card";
import { Badge } from "../ui/badge";
import {
  User,
  Phone,
  Wallet,
  Mail,
  CheckCircle2,
  XCircle,
  Edit,
  Trash2,
  Calendar,
  FileText,
  AlertCircle,
} from "lucide-react";
import { IMG_URl } from "src/common/img_url";
import UAEDirhamIcon from "../utils/UAEDirhamIcon";

function CustomerCard(props) {
  const { t } = useTranslation();
  const { setCustomer, deleteCustomerFunction, lang } = indexStore();
  const editCustomer = (e) => {
    setCustomer(e);
  };
  const onDelete = (id) => {
    deleteCustomerFunction(id);
  };

  useEffect(() => {
    const handleContextMenu = (e) => e.preventDefault();
    const handleKeyDown = (e) => {
      if (e.ctrlKey && (e.key === "c" || e.key === "u" || e.key === "s")) {
        e.preventDefault();
      }
    };

    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const per = localStorage.getItem("role");
  const customer = props.element;
  const name = lang === "ar" ? customer.name_ar || customer.name_en : customer.name_en || customer.name_ar;

  return (
    <Card
      style={{ userSelect: "none" }}
      className="group hover:shadow-xl transition-all duration-300 overflow-hidden border-2 hover:border-primary/20"
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4 flex-1">
            {/* Avatar/Image */}
            <div className="relative shrink-0">
              {customer.image ? (
                <img
                  src={`${IMG_URl}/${customer.image}`}
                  alt={name}
                  className="w-16 h-16 rounded-full object-cover border-2 shadow-sm"
                />
              ) : (
                <div className="w-16 h-16 rounded-full border-2 bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                  <User className="w-8 h-8 text-primary" />
                </div>
              )}
            </div>

            {/* Name and Status */}
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-bold text-foreground line-clamp-1 mb-1">
                {name || t("gloabl.unknown") || "Unknown"}
              </h3>
              <Badge
                variant="outline"
                className={
                  customer.active
                    ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300"
                    : "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300"
                }
              >
                {customer.active ? (
                  <>
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    {t("gloabl.active")}
                  </>
                ) : (
                  <>
                    <XCircle className="w-3 h-3 mr-1" />
                    {t("gloabl.inactive")}
                  </>
                )}
              </Badge>
            </div>
          </div>

          {/* Actions */}
          <Visibility visible={per === userTypes.ADMIN}>
            <div className="flex gap-1 shrink-0">
              <EditCustomerModel
                trigger={
                  <button
                    onClick={() => editCustomer(customer)}
                    className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                    title={t("gloabl.edit")}
                  >
                    <Edit className="w-4 h-4 text-muted-foreground" />
                  </button>
                }
              />
              <AlertDialogs
                trigger={
                  <button
                    className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                    title={t("gloabl.delete")}
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                }
                variant="icon"
                onClickOk={() => onDelete(customer.id)}
                description={<span>{t("gloabl.deleteMessage")}</span>}
                title={`${t("gloabl.delete")} ${t("gloabl.customer")}`}
              />
            </div>
          </Visibility>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Phone */}
        <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
          <div className="p-1.5 rounded-lg bg-blue-100 dark:bg-blue-900/30">
            <Phone className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground mb-0.5">
              {t("gloabl.phone")}
            </p>
            <p className="text-sm font-semibold text-foreground">
              {customer.phone || "—"}
            </p>
          </div>
        </div>

        {/* Email */}
        {customer.email && (
          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
            <div className="p-1.5 rounded-lg bg-purple-100 dark:bg-purple-900/30">
              <Mail className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-muted-foreground mb-0.5">
                {t("gloabl.email") || "Email"}
              </p>
              <p className="text-sm font-semibold text-foreground truncate">
                {customer.email}
              </p>
            </div>
          </div>
        )}

        {/* Wallet - Only visible to Admin */}
        <Visibility visible={per === userTypes.ADMIN}>
          <div className="flex items-center gap-3 p-3 rounded-lg bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border border-amber-200/50 dark:border-amber-800/50">
            <div className="p-1.5 rounded-lg bg-amber-100 dark:bg-amber-900/30">
              <Wallet className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-muted-foreground mb-0.5">
                {t("gloabl.wallet")}
              </p>
              <p className="text-sm font-bold text-foreground flex items-center gap-1">
                <UAEDirhamIcon className="w-4 h-4" />
                {customer.wallet !== null && customer.wallet !== undefined
                  ? Number(customer.wallet).toFixed(2)
                  : "0.00"}
              </p>
            </div>
          </div>
        </Visibility>

        {/* Deletion Info - Only visible if is_deleted is true */}
        {customer.is_deleted && (
          <div className="space-y-2 pt-2 border-t border-red-200 dark:border-red-800">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
              <p className="text-sm font-semibold text-red-600 dark:text-red-400">
                {t("gloabl.deleted_account") || "Deleted Account"}
              </p>
            </div>

            {/* Deleted Date */}
            {customer.deleted_at && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200/50 dark:border-red-800/50">
                <div className="p-1.5 rounded-lg bg-red-100 dark:bg-red-900/30">
                  <Calendar className="w-4 h-4 text-red-600 dark:text-red-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground mb-0.5">
                    {t("gloabl.deleted_at") || "Deleted At"}
                  </p>
                  <p className="text-sm font-semibold text-foreground">
                    {new Date(customer.deleted_at).toLocaleString(lang === "ar" ? "ar-SA" : "en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                </div>
              </div>
            )}

            {/* Delete Reason */}
            {customer.delete_reason && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200/50 dark:border-red-800/50">
                <div className="p-1.5 rounded-lg bg-red-100 dark:bg-red-900/30 shrink-0">
                  <FileText className="w-4 h-4 text-red-600 dark:text-red-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground mb-0.5">
                    {t("gloabl.delete_reason") || "Delete Reason"}
                  </p>
                  <p className="text-sm font-semibold text-foreground break-words">
                    {customer.delete_reason}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default CustomerCard;

export const CustomerCardLoading = () => {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-4">
          <Skeleton className="w-16 h-16 rounded-full shrink-0" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-4 w-20" />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <Skeleton className="h-16 w-full rounded-lg" />
          <Skeleton className="h-16 w-full rounded-lg" />
          <Skeleton className="h-16 w-full rounded-lg" />
        </div>
      </CardContent>
    </Card>
  );
};
